import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Share } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmDialogProvider } from './context/ConfirmDialogContext';
import { fetchApi } from './config/api';
import { trackVisit } from './utils/trackVisit';
import { AppDrawer, BottomNav } from './components/AppNavigation';
import { Sidebar } from './components/Sidebar';
import { AccountDeletionBanner } from './components/AccountDeletionBanner';
import { NotificationBell } from './components/NotificationBell';
import { Loading, Sheet } from './components/UI';
import { useInstallPrompt } from './components/useInstallPrompt';
import LandingPage from './pages/Landing';
import { LEGAL_PATHS } from './legal/paths';

const PricingPage = lazy(() => import('./pages/Pricing'));
const LegalPage = lazy(() => import('./pages/Legal'));

// Angemeldete Seiten erst beim Navigieren nachladen statt in ein einziges grosses Bundle zu
// packen - die Landing Page bleibt bewusst eager, da sie fuer nicht angemeldete Besucher der
// allererste Inhalt ist.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Termine = lazy(() => import('./pages/Termine'));
const Notizen = lazy(() => import('./pages/Notizen'));
const Interessenten = lazy(() => import('./pages/Interessenten'));
const InteressentDetail = lazy(() => import('./pages/InteressentDetail'));
const Kaufvertrag = lazy(() => import('./pages/Kaufvertrag'));
const DogForm = lazy(() => import('./pages/DogForm'));
const DogDetail = lazy(() => import('./pages/DogDetail'));
const PregnancyCalendar = lazy(() => import('./pages/PregnancyCalendar'));
const BreedingPlan = lazy(() => import('./pages/BreedingPlan'));
const VetSummary = lazy(() => import('./pages/VetSummary'));
const BreedingStationForm = lazy(() => import('./pages/BreedingStationForm'));
const JoinBreedingStation = lazy(() => import('./pages/JoinBreedingStation'));
const DogTransfers = lazy(() => import('./pages/DogTransfers'));
const TakeOverDog = lazy(() => import('./pages/TakeOverDog'));
const Account = lazy(() => import('./pages/Account'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminDogDetail = lazy(() => import('./pages/admin/AdminDogDetail'));
const AdminBackgroundTaskDetail = lazy(() => import('./pages/admin/AdminBackgroundTaskDetail'));
const Pedigree = lazy(() => import('./pages/Pedigree'));
const DnaAnalyses = lazy(() => import('./pages/DnaAnalyses'));
const DnaImportWizard = lazy(() => import('./pages/DnaImportWizard'));
const DnaSubjectDetail = lazy(() => import('./pages/DnaSubjectDetail'));
const Support = lazy(() => import('./pages/Support'));
const SupportDetail = lazy(() => import('./pages/SupportDetail'));

const PageLoading: React.FC = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <Loading />
  </div>
);

// Das Poster ist ein Tab der Wurf-Seite; alte Links auf die frühere eigene Seite führen dorthin.
const PosterRedirect: React.FC = () => {
  const id = new URLSearchParams(useLocation().search).get('id');
  return <Navigate to={id ? `/breeding-plan?id=${id}&tab=poster` : '/dashboard'} replace />;
};

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoading />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" />;
};

// Admin-only route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return user?.isAdmin ? <>{children}</> : <Navigate to="/dashboard" />;
};

// Generalisierter Rollen-Guard: zeigt Kinder nur wenn User mindestens eine der
// angegebenen Rollen hat. Wird fuer Admin und zukuenftige Zuechter-Routen genutzt.
// Aktuell nur von AdminRoute abaixo konsumiert; wird in Phase 2 fuer Zuechter-Routen
// direkt in den Route-Definitionen verwendet.
export const RoleRoute: React.FC<{ roles: string[]; children: React.ReactNode }> = ({ roles, children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  const allowed = roles.some((role) => user?.roles.includes(role) ?? false);
  return allowed ? <>{children}</> : <Navigate to="/dashboard" />;
};

const AppContent: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const navigate = useNavigate();
  const [breedingStationName, setBreedingStationName] = useState<string>('');
  const [hasPedigreeData, setHasPedigreeData] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const { isAvailable: canInstall, canPrompt, promptInstall } = useInstallPrompt();

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate('/');
  };

  const handleInstall = () => {
    setDrawerOpen(false);
    // Auf iOS gibt es keinen Dialog - dort hilft nur die Anleitung
    if (canPrompt) {
      promptInstall();
    } else {
      setIosHintOpen(true);
    }
  };

  // Anonyme Herkunftszaehlung: einmal pro Browser-Sitzung, sobald der Auth-Status feststeht.
  useEffect(() => {
    if (!isLoading) trackVisit(isAuthenticated);
  }, [isLoading, isAuthenticated]);

  // Zuchtstätte beim Laden laden
  useEffect(() => {
    const loadBreedingStation = async () => {
      if (!isAuthenticated) {
        setBreedingStationName('');
        return;
      }
      try {
        const data = await fetchApi('/breeding-stations');
        if (data && data.length > 0) {
          setBreedingStationName(data[0].name);
        }
      } catch {
        // Keine Zuchtstätte vorhanden (oder Session abgelaufen -> fetchApi hat bereits zum Login geschickt)
      }
    };
    loadBreedingStation();
  }, [isAuthenticated]);

  // Menüpunkt "Ahnentafel" nur anzeigen, wenn es dafür ueberhaupt ausreichende Daten gibt
  // (mind. ein Hund mit einem verknuepften Elternteil in mind. einer Rasse).
  useEffect(() => {
    const loadPedigreeAvailability = async () => {
      if (!isAuthenticated) {
        setHasPedigreeData(false);
        return;
      }
      try {
        const res = await fetchApi('/pedigree/breeds');
        const breeds = res?.data ?? [];
        setHasPedigreeData(Array.isArray(breeds) && breeds.length > 0);
      } catch {
        setHasPedigreeData(false);
      }
    };
    loadPedigreeAvailability();
  }, [isAuthenticated]);

  // Solange der Auth-Status noch nicht feststeht, weder die Landing Page noch die
  // angemeldete Shell rendern - sonst würden Sidebar/Routes kurz mit einem noch
  // unbekannten Auth-Status mounten (Sidebar feuert sofort einen /dogs-Request, und
  // <Navigate to="/dashboard"> ändert die URL), bevor feststeht, dass gar kein Token
  // vorhanden ist. Das 401 vom verfrühten Request löst dann handleUnauthorized() aus,
  // das per window.location.href auf "/" hart neu lädt - und der ganze Zyklus beginnt
  // von vorn (Endlosschleife bei frischem, abgemeldetem Besuch).
  if (isLoading) {
    return <PageLoading />;
  }

  // Show landing page (or other public marketing pages) when not authenticated
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/pakete" element={<PricingPage />} />
          <Route path={LEGAL_PATHS.terms} element={<LegalPage kind="terms" public />} />
          <Route path={LEGAL_PATHS.privacy} element={<LegalPage kind="privacy" public />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </Suspense>
    );
  }

  // Logged in - show dashboard
  return (
    <div className="min-h-screen bg-dark-900 text-dark-50 md:flex">
      <Sidebar
        stationName={breedingStationName}
        roles={user?.roles ?? []}
        isAdmin={!!user?.isAdmin}
        hasPedigreeData={hasPedigreeData}
        onLogout={handleLogout}
        onInstall={canInstall ? handleInstall : undefined}
      />

      <div className="min-w-0 flex-1">
      <header className="app-header md:hidden">
        <div className="container mx-auto flex h-14 items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 shrink items-center gap-3">
            <Link to="/dashboard" className="inline-flex shrink-0 items-center gap-2 sm:gap-3">
              <img src="/canebase-icon.png" alt="" className="h-8 w-auto shrink-0 object-contain sm:h-9" />
              <span className="text-base font-bold tracking-tight text-gradient sm:text-lg">canebase</span>
            </Link>
          </div>

          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              type="button"
              className="icon-btn -mr-2"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav:aria.openMenu')}
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop: schmale Leiste oben rechts fuer die Benachrichtigungen (mobil sitzt die Glocke in der Kopfzeile oben). */}
      <div className="app-header hidden h-12 items-center justify-end px-6 md:flex">
        <NotificationBell />
      </div>

      <AppDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userName={user?.name || ''}
        userEmail={user?.email || ''}
        stationName={breedingStationName}
        roles={user?.roles ?? []}
        isAdmin={!!user?.isAdmin}
        hasPedigreeData={hasPedigreeData}
        onLogout={handleLogout}
        onInstall={canInstall ? handleInstall : undefined}
      />

      <Sheet isOpen={iosHintOpen} title={t('nav:drawer.installApp')} onClose={() => setIosHintOpen(false)} size="sm">
        <div className="space-y-4 text-sm text-dark-200">
          <p>{t('nav:installHint.intro')}</p>
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-xs font-bold text-primary-300">
                1
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                {t('nav:installHint.step1Before')} <Share size={16} className="inline text-primary-300" /> {t('nav:installHint.step1After')}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-xs font-bold text-primary-300">
                2
              </span>
              <span>{t('nav:installHint.step2')}</span>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-xs font-bold text-primary-300">
                3
              </span>
              <span>{t('nav:installHint.step3')}</span>
            </li>
          </ol>
          <p className="text-xs text-dark-400">{t('nav:installHint.note')}</p>
        </div>
      </Sheet>

       <main className="app-main">
        <AccountDeletionBanner />
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/termine" element={
            <ProtectedRoute>
              <Termine />
            </ProtectedRoute>
          } />
          <Route path="/notizen" element={
            <ProtectedRoute>
              <Notizen />
            </ProtectedRoute>
          } />
          <Route path="/interessenten" element={
            <ProtectedRoute>
              <Interessenten />
            </ProtectedRoute>
          } />
          <Route path="/interessenten/:id" element={
            <ProtectedRoute>
              <InteressentDetail />
            </ProtectedRoute>
          } />
          <Route path="/kaufvertraege/:id" element={
            <ProtectedRoute>
              <Kaufvertrag />
            </ProtectedRoute>
          } />
          <Route path="/dog-form" element={
            <ProtectedRoute>
              <DogForm />
            </ProtectedRoute>
          } />
          <Route path="/dogs/new" element={
            <ProtectedRoute>
              <DogForm />
            </ProtectedRoute>
          } />
          <Route path="/dogs/:id" element={
            <ProtectedRoute>
              <DogDetail />
            </ProtectedRoute>
          } />
          <Route path="/dogs/:id/pregnancy" element={
            <ProtectedRoute>
              <PregnancyCalendar />
            </ProtectedRoute>
          } />
          <Route path="/breeding-plan" element={
            <ProtectedRoute>
              <BreedingPlan />
            </ProtectedRoute>
          } />
          <Route path="/breeding-plan/poster" element={<PosterRedirect />} />
          <Route path="/breeding-plan/vet-summary" element={
            <ProtectedRoute>
              <VetSummary />
            </ProtectedRoute>
          } />
          <Route path="/breeding-station" element={
            <ProtectedRoute>
              <BreedingStationForm />
            </ProtectedRoute>
          } />
          <Route path="/uebergaben" element={
            <ProtectedRoute>
              <DogTransfers />
            </ProtectedRoute>
          } />
          <Route path="/hund-uebernehmen" element={
            <ProtectedRoute>
              <TakeOverDog />
            </ProtectedRoute>
          } />
          <Route path="/breeding-station/join" element={
            <ProtectedRoute>
              <JoinBreedingStation />
            </ProtectedRoute>
          } />
          <Route path="/account" element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          } />
          <Route path="/pedigree" element={
            <ProtectedRoute>
              <Pedigree />
            </ProtectedRoute>
          } />
          <Route path="/dna" element={
            <RoleRoute roles={['DnaAuswertung', 'Admin']}>
              <DnaAnalyses />
            </RoleRoute>
          } />
          <Route path="/dna/import" element={
            <RoleRoute roles={['DnaAuswertung', 'Admin']}>
              <DnaImportWizard />
            </RoleRoute>
          } />
          <Route path="/dna/:type/:id" element={
            <RoleRoute roles={['DnaAuswertung', 'Admin']}>
              <DnaSubjectDetail />
            </RoleRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute>
              <Support />
            </ProtectedRoute>
          } />
          <Route path="/support/:id" element={
            <ProtectedRoute>
              <SupportDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/:tab" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/users/:id" element={
            <AdminRoute>
              <AdminUserDetail />
            </AdminRoute>
          } />
          <Route path="/admin/dogs/:id" element={
            <AdminRoute>
              <AdminDogDetail />
            </AdminRoute>
          } />
          <Route path="/admin/backgroundtasks/:id" element={
            <AdminRoute>
              <AdminBackgroundTaskDetail />
            </AdminRoute>
          } />
          <Route path={LEGAL_PATHS.terms} element={<LegalPage kind="terms" />} />
          <Route path={LEGAL_PATHS.privacy} element={<LegalPage kind="privacy" />} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
        </Suspense>
      </main>

      <BottomNav />
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <ConfirmDialogProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ConfirmDialogProvider>
    </Router>
  );
}

export default App;
